/**
 * Native BLE keep-alive session.
 *
 * Hold one SPP socket while the app is foreground. Reconnect on MAC switch
 * or write failure. Disconnect on background / logout only.
 *
 * Every print re-runs connectPrinter() before writing. Natively that is an
 * instant no-op while the socket is held, and a full connect when something
 * else closed it, so the stale JS "connected" flag is never trusted. Writes
 * pass the wrapper's onError hook so a dead socket surfaces as a rejection
 * instead of a silent success. On failure the session is torn down and the
 * warmup sequence (scan, then connect) runs once more before giving up.
 */
import { AppState, Platform } from 'react-native';
import { Buffer } from 'buffer';
import {
  NativeBleTransport,
  ThermalDocument,
  discoverBleDevices,
  ensureNativeBleReady,
  loadBlePrinter,
  print,
} from './thermalPrintCore';
import type { ConnectionConfig } from './thermalPrintCore';
import { hasBluetoothPermissions } from '../utils/bluetoothPermissions';

type BleSession = {
  mac: string;
  transport: NativeBleTransport;
};

const LOG = '[ble-session]';
const DISCOVERY_DURATION_S = 5;
/** Native reports write errors within milliseconds; this bounds the wait. */
const WRITE_ERROR_GRACE_MS = 1500;

let session: BleSession | null = null;
let appStateSub: { remove: () => void } | null = null;
let jobChain: Promise<void> = Promise.resolve();

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err ?? 'unknown error');
}

function normalizeMac(macAddress: string): string {
  return String(macAddress || '')
    .trim()
    .toUpperCase();
}

function bleConfig(mac: string): ConnectionConfig {
  return { type: 'bluetooth', macAddress: mac };
}

/** Bluetooth off or permission missing: a reconnect cannot fix these. */
function isRetryable(err: unknown): boolean {
  const text = errText(err).toLowerCase();
  return !text.includes('not enabled') && !text.includes('permission');
}

function ensureAppStateHook() {
  if (appStateSub || Platform.OS === 'web') return;
  appStateSub = AppState.addEventListener('change', next => {
    if (next === 'background') {
      void disconnectNativeBleSession();
    }
  });
}

export async function disconnectNativeBleSession(): Promise<void> {
  const current = session;
  session = null;
  if (!current) return;
  try {
    await current.transport.disconnect();
  } catch {
    /* ignore teardown errors */
  }
}

/**
 * Open or verify the socket. connectPrinter() returns immediately when the
 * native adapter still holds the socket, so this is cheap on the hot path.
 */
async function connectSession(mac: string): Promise<BleSession> {
  const key = normalizeMac(mac);

  if (session && session.mac !== key) {
    await disconnectNativeBleSession();
  }

  await ensureNativeBleReady(mac);
  const transport = session?.transport ?? new NativeBleTransport();
  try {
    await transport.connect(bleConfig(mac));
  } catch (err) {
    await disconnectNativeBleSession();
    throw err;
  }
  session = { mac: key, transport };
  ensureAppStateHook();
  return session;
}

/**
 * Same write the print-core transport does, with the wrapper's onError hook
 * attached. printRawBase64() (added by the app's bridge patch) sends the bytes
 * as encoded; printBill() would re-encode them as UTF-8 and garble the logo
 * raster. There is no success callback, so success is "no error within the
 * grace window".
 */
async function writeBytes(bytes: Uint8Array): Promise<void> {
  const printer = await loadBlePrinter();
  const payload = Buffer.from(bytes).toString('base64');
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve();
    }, WRITE_ERROR_GRACE_MS);
    printer.printRawBase64(payload, {
      onError: err => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    });
  });
}

async function writeWithReconnect(
  doc: ThermalDocument,
  mac: string,
): Promise<void> {
  const bytes = doc.encode();

  const writeOnce = async () => {
    await connectSession(mac);
    await writeBytes(bytes);
  };

  try {
    await writeOnce();
  } catch (err) {
    console.log(LOG, 'print failed:', errText(err));
    await disconnectNativeBleSession();
    if (!isRetryable(err)) throw err;

    // Same wake sequence the warmup and the settings screen use. Android
    // only: discoverDevices() exists only in the patch's Java code.
    if (Platform.OS === 'android') {
      try {
        await discoverBleDevices({ duration: DISCOVERY_DURATION_S });
      } catch (scanErr) {
        console.log(LOG, 'discovery:', errText(scanErr));
      }
    }
    console.log(LOG, 'reconnecting and retrying once');
    await writeOnce();
  }
}

export async function printNativeBleKeepAlive(
  doc: ThermalDocument,
  macAddress: string,
): Promise<void> {
  const mac = String(macAddress || '').trim();
  if (!mac) {
    throw new Error('No Bluetooth printers selected');
  }

  if (Platform.OS === 'web') {
    await print(doc, bleConfig(mac));
    return;
  }

  const run = jobChain.then(() => writeWithReconnect(doc, mac));
  jobChain = run.then(
    () => undefined,
    () => undefined,
  );
  await run;
}

/**
 * Settings-screen wake, without the settings screen. Logs, never throws.
 * Android only: its discovery step exists only in the patch's Java code, so
 * on iOS it threw before connecting; iOS prints connect on demand.
 */
export async function warmupNativeBleSession(
  macAddress: string,
): Promise<void> {
  const mac = String(macAddress || '').trim();
  if (!mac || Platform.OS !== 'android') return;

  const run = jobChain.then(async () => {
    try {
      if (!(await hasBluetoothPermissions())) {
        console.log(LOG, 'warmup skipped: no Bluetooth permission');
        return;
      }
      const key = normalizeMac(mac);
      if (session?.mac === key && session.transport.isConnected()) {
        console.log(LOG, 'warmup skipped: session already open');
        return;
      }

      await discoverBleDevices({ duration: DISCOVERY_DURATION_S });
      await connectSession(mac);
      console.log(LOG, 'warm session ready for', key);
    } catch (err) {
      console.log(LOG, 'warmup failed:', errText(err));
    }
  });
  jobChain = run.then(
    () => undefined,
    () => undefined,
  );
  await run;
}
